// Summary jobs, one per video (the visible one is selected by the route; the others keep
// running in the background). The workflow of one job:
//   1. GET /api/summary (fast path, works without any login; explicit language or preference)
//   2. no Codex login available → error `codex_auth_required` (the UI opens the login dialog and resumes)
//   3. wait for a free slot (at most MAX_CONCURRENT jobs run at the same time)
//   4. client mode: fetch the transcript in the browser
//   5. POST /api/summarize and follow the event stream: progress while the model thinks, then
//      `status: writing` (onWriting: the summary can be read now) and the text as it is written.
//      The browser-stored login is read from storage right before sending; the server refreshes
//      it only before its request and de-duplicates concurrent refreshes, so any number of runs
//      may send the same login at the same time (a refreshed copy comes back as `auth` event).
//   6. server mode and YouTube blocks the server (`youtube_blocked`): fetch the transcript in the
//      browser and run step 5 once more with it
//
// Every summary runs as a job on the server that does not depend on the connection (see the
// contract of POST /api/summarize). When the stream breaks (app minimized on Android, network
// change), the job keeps running here: it re-attaches with GET /api/summarize/jobs/:jobId?after=N
// (backoff, and right away when the app comes back to the foreground or online) and only fails
// once the server job cannot exist anymore. An unknown job (server restart) is looked up in the
// cache and otherwise started once more. Running jobs are kept in localStorage, so a reloaded
// page re-attaches to them (resumePersisted) instead of starting new runs; a re-attached job gets
// the text written so far in one piece and follows the rest live. Only an explicit cancel
// (button, closing the tab, replacing the job) cancels the server job (DELETE); leaving the page
// does not.

import {
  SUMMARY_JOB_MAX_RUN_MS,
  SUMMARY_JOB_RETENTION_MS,
  type CodexAuthBlob,
  type LangPreference,
  type PushTarget,
  type SummarizeRequest,
  type SummarizeStage,
  type SummaryLang,
  type SummaryRecord,
  type TranscriptMode,
  type VideoMeta,
} from '../../shared/api';
import {
  ApiRequestError,
  attachSummaryJob,
  cancelSummaryJob,
  getCachedSummary,
  isAbortError,
  summarize,
  type IndexedSummarizeEvent,
  type StreamOptions,
} from './api';
import { createSemaphore, type Semaphore } from './locks';
import {
  isOlderCodexAuth,
  loadPersistedJobs,
  removePersistedJob,
  savePersistedJob,
  type PersistedJob,
} from './storage';
import { RECONNECT_TEXTS, rateLimitText, type JobErrorCode } from './texts';
import { BrowserTranscriptError, fetchTranscriptInBrowser } from './transcript';
import { rememberTranscript } from './transcriptLoader';
import { fetchYouTubeOEmbed } from './youtube';

export type JobStatus = 'running' | 'done' | 'error' | 'cancelled';
export type JobSource = 'history' | 'server-cache' | 'generated';

export interface JobError {
  code: Exclude<JobErrorCode, 'aborted'>;
  /** More specific user-facing text than the default one of `code`. */
  message?: string;
  /** Technical detail (server message), shown collapsed. */
  detail?: string;
}

export const SERVER_BLOCKED_NOTICE = 'YouTube hat den Server blockiert – Transkript wurde im Browser geladen';

/**
 * At most this many summaries run at the same time. Each holds a streaming connection (HTTP/2
 * multiplexes them) and, in client mode, loads its transcript from YouTube in the browser.
 */
export const MAX_CONCURRENT = 6;

/** After this long since its start a server job cannot exist anymore: stop re-attaching. */
export const JOB_LIFETIME_MS = SUMMARY_JOB_MAX_RUN_MS + SUMMARY_JOB_RETENTION_MS;
/** Waits between re-attach attempts (the last one repeats). */
export const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 15_000, 20_000, 30_000];
/** The server sends a keep-alive every 15 s: a stream silent for longer is dead. */
const STREAM_IDLE_TIMEOUT_MS = 45_000;
/** On returning to the app, a stream silent for this long is replaced right away. */
const STREAM_STALE_ON_WAKE_MS = 20_000;
/** Max. wait for the push subscription before sending a request without it. */
const PUSH_TARGET_TIMEOUT_MS = 3_000;

export interface Job {
  /** Increments with every run; stale async updates are dropped. */
  runId: number;
  videoId: string;
  /** Language asked for explicitly (address, regenerate); undefined: resolved from `langPref`. */
  requestedLang?: SummaryLang;
  /** Effective summary language; undefined until the server has resolved it. */
  lang?: SummaryLang;
  /** Base code of the video's spoken language, if known. */
  sourceLang?: string;
  force: boolean;
  /** Transcript mode of this run (undefined until the context is known). */
  mode?: TranscriptMode;
  status: JobStatus;
  stage: SummarizeStage;
  stageMessage?: string;
  progress?: string;
  /** Waiting for a free slot before it starts. */
  waiting?: 'slot';
  /** The connection to the server job broke; re-attaching automatically. */
  reconnecting?: boolean;
  /** The server job of this run, once known. */
  jobId?: string;
  /** Subtle remark about how the summary came about (e.g. the browser fallback). */
  notice?: string;
  /** YouTube refused the server's transcript request in this run. */
  serverBlocked?: boolean;
  meta?: Partial<VideoMeta>;
  markdown: string;
  summary?: SummaryRecord;
  source?: JobSource;
  error?: JobError;
  startedAt: number;
  endedAt?: number;
}

export interface JobContext {
  signedIn: boolean;
  /** Codex login stored on the server (signed-in users). */
  serverCodex: boolean;
  /** Browser-stored Codex login. */
  localAuth: CodexAuthBlob | null;
  mode: TranscriptMode;
  /** The user's model and reasoning effort for generating. */
  model?: string | undefined;
  effort?: string | undefined;
}

export interface StartOptions {
  videoId: string;
  /** Explicit summary language. */
  lang?: SummaryLang | undefined;
  /** Used when `lang` is not given. */
  langPref: LangPreference;
  force?: boolean;
  /** Skip the GET /api/summary fast path (e.g. when resuming after login). */
  skipCache?: boolean;
  /** Override the transcript mode for this run. */
  mode?: TranscriptMode;
  meta?: Partial<VideoMeta>;
}

export interface JobCallbacks {
  getContext(): Promise<JobContext>;
  /** The newest browser-stored login, read right before it is sent. */
  getLocalAuth(): CodexAuthBlob | null;
  onAuthRefresh(auth: CodexAuthBlob): void;
  /**
   * A generated summary can be read now: it is being written (`status: writing`). Called once
   * per run, at the latest right before onDone of a generated summary.
   */
  onWriting?(job: Job): void;
  onDone(summary: SummaryRecord, job: Job): void;
  onError(error: JobError, job: Job): void;
  /** Web Push target sent with POST /api/summarize ("summary ready" while the app is closed). */
  getPushTarget?(): Promise<PushTarget | undefined>;
}

/** Where running server jobs are kept across reloads. */
export interface JobStore {
  load(): PersistedJob[];
  save(job: PersistedJob): void;
  /** Removes the job of a video (only if it is `jobId`, when given). */
  remove(videoId: string, jobId?: string): void;
}

/** Subscribes to "the app is back / online" moments; returns the unsubscribe function. */
export type WakeSource = (listener: () => void) => () => void;

export interface JobManagerOptions {
  maxConcurrent?: number;
  /** Default: localStorage (`yts:jobs`). */
  store?: JobStore;
  /** Default: visibilitychange → visible, online, focus, pageshow. */
  wakeSource?: WakeSource;
  /** Default: RECONNECT_DELAYS_MS. */
  reconnectDelaysMs?: number[];
  now?: () => number;
}

const localJobStore: JobStore = {
  load: loadPersistedJobs,
  save: savePersistedJob,
  remove: removePersistedJob,
};

const documentWakeSource: WakeSource = (listener) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  const onVisibility = () => {
    if (document.visibilityState === 'visible') listener();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', listener);
  window.addEventListener('focus', listener);
  window.addEventListener('pageshow', listener);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', listener);
    window.removeEventListener('focus', listener);
    window.removeEventListener('pageshow', listener);
  };
};

const STAGE_ORDER: SummarizeStage[] = ['cache', 'transcript', 'summarizing', 'writing'];

export function stageIndex(stage: SummarizeStage): number {
  return STAGE_ORDER.indexOf(stage);
}

function toJobError(error: unknown): JobError {
  if (error instanceof ApiRequestError) {
    const code = error.code === 'aborted' ? 'network' : error.code;
    return { code, detail: error.message, ...(code === 'rate_limited' ? { message: rateLimitText(error.retryAfter) } : {}) };
  }
  if (error instanceof BrowserTranscriptError) {
    const code =
      error.code === 'transcript_unavailable' || error.code === 'video_not_found'
        ? error.code
        : error.code === 'blocked'
          ? 'transcript_blocked'
          : 'transcript_browser';
    return { code, ...(error.userMessage ? { message: error.userMessage } : {}), ...(error.message ? { detail: error.message } : {}) };
  }
  return { code: 'internal', detail: error instanceof Error ? error.message : String(error) };
}

/** Failures after which the server job is presumably still running and can be re-attached. */
function isConnectionError(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return false;
  switch (error.code) {
    case 'network':
    case 'stream_interrupted':
    case 'aborted': // an attempt we gave up on (dead connection), not the run
    case 'rate_limited':
      return true;
    case 'internal':
      // Proxy errors while the server restarts or deploys (502/503/504).
      return error.status !== undefined && error.status >= 500;
    default:
      return false;
  }
}

function definedOnly<T extends object>(value: T | undefined): Partial<T> {
  if (!value) return {};
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined && v !== '')) as Partial<T>;
}

const nextFrame: (callback: () => void) => number =
  typeof requestAnimationFrame === 'function'
    ? (callback) => requestAnimationFrame(callback)
    : (callback) => setTimeout(callback, 16) as unknown as number;
const cancelFrame: (handle: number) => void =
  typeof cancelAnimationFrame === 'function' ? (handle) => cancelAnimationFrame(handle) : (handle) => clearTimeout(handle);

interface Run {
  runId: number;
  controller: AbortController;
  options: StartOptions;
  /** The server job while it runs (cancelled with DELETE when the run is cancelled). */
  jobId?: string;
}

/** A stream attempt that is currently open (for the liveness check on wake). */
interface ActiveStream {
  controller: AbortController;
  lastActivity: number;
  /** Only streams of a known server job may be dropped and re-attached. */
  reattachable: () => boolean;
}

type Outcome = 'finished' | 'blocked' | 'lost';

export type Jobs = Readonly<Record<string, Job>>;

export class JobManager {
  private jobs: Jobs = {};
  private runs = new Map<string, Run>();
  private runCounter = 0;
  private listeners = new Set<() => void>();
  private readonly slots: Semaphore;
  private readonly store: JobStore;
  private readonly reconnectDelays: number[];
  private readonly now: () => number;
  private wakeWaiters = new Set<() => void>();
  private activeStreams = new Set<ActiveStream>();

  constructor(
    private callbacks: JobCallbacks,
    options: JobManagerOptions = {},
  ) {
    this.slots = createSemaphore(options.maxConcurrent ?? MAX_CONCURRENT);
    this.store = options.store ?? localJobStore;
    this.reconnectDelays = options.reconnectDelaysMs?.length ? options.reconnectDelaysMs : RECONNECT_DELAYS_MS;
    this.now = options.now ?? Date.now;
    (options.wakeSource ?? documentWakeSource)(() => this.wake());
  }

  setCallbacks(callbacks: JobCallbacks): void {
    this.callbacks = callbacks;
  }

  // ---- state

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): Jobs => this.jobs;

  get(videoId: string): Job | undefined {
    return this.jobs[videoId];
  }

  private commit(videoId: string, job: Job | null): void {
    const next = { ...this.jobs };
    if (job) next[videoId] = job;
    else delete next[videoId];
    this.jobs = next;
    for (const listener of this.listeners) listener();
  }

  /** Applies an update only if `runId` is still the current run of the video. */
  private patch(videoId: string, runId: number, update: Partial<Job> | ((job: Job) => Partial<Job>)): void {
    const current = this.jobs[videoId];
    if (!current || current.runId !== runId) return;
    this.commit(videoId, { ...current, ...(typeof update === 'function' ? update(current) : update) });
  }

  // ---- actions

  start(options: StartOptions): void {
    this.begin(options);
  }

  /**
   * Re-attaches to the server jobs that were running when the page was left (reload, the app
   * was killed in the background). Call it on startup before starting new jobs, so a video
   * whose job still runs on the server is followed instead of started anew.
   */
  resumePersisted(): void {
    for (const saved of this.store.load()) {
      if (this.jobs[saved.videoId]) continue;
      if (this.now() - saved.jobStartedAt > JOB_LIFETIME_MS) {
        this.store.remove(saved.videoId, saved.jobId);
        continue;
      }
      this.begin(
        {
          videoId: saved.videoId,
          ...(saved.requestedLang ? { lang: saved.requestedLang } : {}),
          langPref: saved.langPref,
          force: saved.force,
          ...(saved.mode ? { mode: saved.mode } : {}),
          ...(saved.title ? { meta: { title: saved.title, ...(saved.channel ? { channel: saved.channel } : {}) } } : {}),
        },
        saved,
      );
    }
  }

  private begin(options: StartOptions, resume?: PersistedJob): void {
    const { videoId } = options;
    this.stopRun(videoId, true);
    const runId = ++this.runCounter;
    const controller = new AbortController();
    this.runs.set(videoId, { runId, controller, options, ...(resume ? { jobId: resume.jobId } : {}) });
    const lang = resume?.lang ?? options.lang;
    this.commit(videoId, {
      runId,
      videoId,
      ...(options.lang ? { requestedLang: options.lang } : {}),
      ...(lang ? { lang } : {}),
      force: !!options.force,
      ...(resume?.mode ? { mode: resume.mode } : {}),
      status: 'running',
      stage: 'cache',
      ...(resume ? { jobId: resume.jobId, stageMessage: RECONNECT_TEXTS.resuming } : {}),
      markdown: '',
      ...(options.meta ? { meta: options.meta } : {}),
      startedAt: resume?.startedAt ?? this.now(),
    });
    void this.execute(runId, options, controller.signal, resume);
    // Title and channel from YouTube right away; fields from the server take precedence.
    if (!options.meta?.title) {
      void fetchYouTubeOEmbed(videoId).then((oembed) => {
        if (oembed) this.patch(videoId, runId, (j) => ({ meta: { ...oembed, ...definedOnly(j.meta) } }));
      });
    }
  }

  /**
   * Aborts the local run of a video. `cancelServerJob`: the user cancels or replaces the summary,
   * so a server job that is still running is cancelled too (never when just leaving the page).
   */
  private stopRun(videoId: string, cancelServerJob: boolean): Run | undefined {
    const run = this.runs.get(videoId);
    if (!run) return undefined;
    run.controller.abort();
    if (cancelServerJob && run.jobId && this.jobs[videoId]?.status === 'running') {
      cancelSummaryJob(run.jobId);
      this.store.remove(videoId, run.jobId);
    }
    return run;
  }

  /** Shows an existing summary (local history) without any request. */
  show(summary: SummaryRecord, meta?: Partial<VideoMeta>): void {
    const { videoId } = summary;
    this.stopRun(videoId, true);
    this.runs.delete(videoId);
    const now = this.now();
    this.commit(videoId, {
      runId: ++this.runCounter,
      videoId,
      lang: summary.lang,
      ...(summary.sourceLang ? { sourceLang: summary.sourceLang } : {}),
      force: false,
      status: 'done',
      stage: 'writing',
      markdown: summary.markdown,
      summary,
      source: 'history',
      meta: { videoId, title: summary.title, channel: summary.channel, ...meta },
      startedAt: now,
      endedAt: now,
    });
  }

  cancel(videoId: string): void {
    const run = this.stopRun(videoId, true);
    if (!run) return;
    this.patch(videoId, run.runId, (j) =>
      j.status === 'running' ? { status: 'cancelled', waiting: undefined, reconnecting: undefined, endedAt: this.now() } : {},
    );
  }

  /** Runs the job of the video again, optionally with changed options (e.g. after login: `{ skipCache: true }`). */
  retry(videoId: string, overrides: Partial<StartOptions> = {}): void {
    const current = this.jobs[videoId];
    const previous = this.runs.get(videoId)?.options;
    if (!current || !previous) return;
    this.start({
      videoId,
      lang: current.requestedLang,
      langPref: previous.langPref,
      force: current.force,
      ...(previous.mode ? { mode: previous.mode } : {}),
      ...(current.meta ? { meta: current.meta } : {}),
      ...overrides,
    });
  }

  /** Cancels and forgets the job of a video (tab closed). */
  remove(videoId: string): void {
    this.stopRun(videoId, true);
    this.runs.delete(videoId);
    if (this.jobs[videoId]) this.commit(videoId, null);
  }

  // ---- reconnecting

  /** The app is back in the foreground or online: retry now, drop dead connections. */
  private wake(): void {
    const now = this.now();
    for (const stream of this.activeStreams) {
      if (stream.reattachable() && now - stream.lastActivity > STREAM_STALE_ON_WAKE_MS) stream.controller.abort();
    }
    const waiters = [...this.wakeWaiters];
    this.wakeWaiters.clear();
    for (const waiter of waiters) waiter();
  }

  /** Resolves after `ms`, or earlier on wake; rejects when `signal` aborts. */
  private waitForRetry(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'));
      const done = () => {
        clearTimeout(timer);
        this.wakeWaiters.delete(done);
        signal.removeEventListener('abort', onAbort);
        resolve();
      };
      const onAbort = () => {
        clearTimeout(timer);
        this.wakeWaiters.delete(done);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      const timer = setTimeout(done, ms);
      this.wakeWaiters.add(done);
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  private async pushTarget(): Promise<PushTarget | undefined> {
    const get = this.callbacks.getPushTarget;
    if (!get) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        get.call(this.callbacks),
        new Promise<undefined>((resolve) => (timer = setTimeout(() => resolve(undefined), PUSH_TARGET_TIMEOUT_MS))),
      ]);
    } catch {
      return undefined;
    } finally {
      clearTimeout(timer);
    }
  }

  // ---- the workflow

  private async execute(runId: number, options: StartOptions, signal: AbortSignal, resume?: PersistedJob): Promise<void> {
    const { videoId, lang, langPref, force = false } = options;
    const isCurrent = () => this.runs.get(videoId)?.runId === runId && !signal.aborted;
    const patch = (update: Partial<Job> | ((job: Job) => Partial<Job>)) => this.patch(videoId, runId, update);
    const run = this.runs.get(videoId);

    // Deltas are batched per animation frame.
    let pendingDelta = '';
    let frame = 0;
    const flushDelta = () => {
      frame = 0;
      if (!pendingDelta) return;
      const text = pendingDelta;
      pendingDelta = '';
      patch((j) => ({ markdown: j.markdown + text }));
    };
    const dropDelta = () => {
      if (frame) cancelFrame(frame);
      frame = 0;
      pendingDelta = '';
    };

    /** The server job of the current stream and the index of the last event received from it. */
    let serverJob: { jobId: string; after: number; startedAt: number } | undefined = resume
      ? { jobId: resume.jobId, after: 0, startedAt: resume.jobStartedAt }
      : undefined;
    let mode: TranscriptMode | undefined = resume?.mode ?? options.mode;
    /** Send the browser-stored login (no server-stored one). */
    let browserLogin = false;
    let announced = false;
    const announce = () => {
      const job = this.jobs[videoId];
      if (announced || job?.runId !== runId) return;
      announced = true;
      this.callbacks.onWriting?.(job);
    };

    const persist = () => {
      const job = this.jobs[videoId];
      if (!serverJob || !job || job.runId !== runId || job.status !== 'running') return;
      this.store.save({
        videoId,
        jobId: serverJob.jobId,
        jobStartedAt: serverJob.startedAt,
        startedAt: job.startedAt,
        ...(mode ? { mode } : {}),
        ...(lang ? { requestedLang: lang } : {}),
        ...(job.lang ? { lang: job.lang } : {}),
        langPref,
        force,
        ...(job.meta?.title ? { title: job.meta.title } : {}),
        ...(job.meta?.channel ? { channel: job.meta.channel } : {}),
      });
    };
    const forget = () => {
      if (serverJob) this.store.remove(videoId, serverJob.jobId);
      if (run?.runId === runId) delete run.jobId;
    };

    const finish = (summary: SummaryRecord, source: JobSource) => {
      if (frame) cancelFrame(frame);
      flushDelta();
      forget();
      patch((j) => ({
        status: 'done',
        waiting: undefined,
        reconnecting: undefined,
        summary,
        source,
        lang: summary.lang || j.lang,
        ...(summary.sourceLang ? { sourceLang: summary.sourceLang } : {}),
        markdown: summary.markdown,
        meta: { ...j.meta, videoId, title: j.meta?.title || summary.title, channel: j.meta?.channel || summary.channel },
        endedAt: this.now(),
      }));
      if (source === 'generated') announce();
      const done = this.jobs[videoId];
      if (done?.runId === runId) this.callbacks.onDone(summary, done);
    };

    const fail = (error: JobError) => {
      if (frame) cancelFrame(frame);
      flushDelta();
      forget();
      patch({ status: 'error', waiting: undefined, reconnecting: undefined, error, endedAt: this.now() });
      const failed = this.jobs[videoId];
      if (failed?.runId === runId) this.callbacks.onError(error, failed);
    };

    // Client mode: the browser fetches the transcript.
    const request: SummarizeRequest = { videoId, ...(lang ? { lang } : { langPref }), ...(force ? { force: true } : {}) };
    const loadTranscript = async (): Promise<boolean> => {
      const transcript = await fetchTranscriptInBrowser(videoId, { signal, preferredLang: lang ?? langPref.fallback });
      rememberTranscript(transcript);
      if (!isCurrent()) return false;
      request.transcript = transcript;
      patch((j) => ({
        meta: {
          ...j.meta,
          videoId,
          title: transcript.title,
          channel: transcript.channel,
          ...(transcript.channelUrl ? { channelUrl: transcript.channelUrl } : {}),
          ...(transcript.durationSeconds ? { durationSeconds: transcript.durationSeconds } : {}),
          ...(transcript.publishedAt ? { publishedAt: transcript.publishedAt } : {}),
        },
      }));
      return true;
    };

    /** Login and transcript mode from the context; false when the job failed or is stale. */
    const prepare = async (): Promise<boolean> => {
      const context = await this.callbacks.getContext();
      if (!isCurrent()) return false;
      mode = options.mode ?? context.mode;
      patch({ mode });
      const hasCodex = context.signedIn ? context.serverCodex || !!context.localAuth : !!context.localAuth;
      if (!hasCodex) {
        fail({ code: 'codex_auth_required' });
        return false;
      }
      browserLogin = !(context.signedIn && context.serverCodex);
      if (context.model) request.model = context.model;
      if (context.effort) request.effort = context.effort;
      return true;
    };

    /** Applies one event; returns the outcome when the stream is over. */
    const handle = (event: IndexedSummarizeEvent, canFallBack: boolean): Outcome | undefined => {
      if (serverJob && event.index !== undefined && event.index > serverJob.after) serverJob.after = event.index;
      switch (event.type) {
        case 'job':
          if (serverJob?.jobId !== event.jobId) {
            serverJob = { jobId: event.jobId, after: event.index ?? 0, startedAt: this.now() };
            if (run?.runId === runId) run.jobId = event.jobId;
            patch({ jobId: event.jobId });
            persist();
          }
          return undefined;
        case 'status':
          patch((j) => ({
            stage: stageIndex(event.stage) >= stageIndex(j.stage) ? event.stage : j.stage,
            stageMessage: event.message,
            ...(event.stage === 'writing' ? { progress: undefined } : {}),
          }));
          if (event.stage === 'writing') announce();
          return undefined;
        case 'lang':
          patch({ lang: event.lang, ...(event.sourceLang ? { sourceLang: event.sourceLang } : {}) });
          persist();
          return undefined;
        case 'meta':
          patch((j) => ({ meta: { ...j.meta, ...event.meta } }));
          persist();
          return undefined;
        case 'delta':
          pendingDelta += event.text;
          if (!frame) frame = nextFrame(flushDelta);
          return undefined;
        case 'progress':
          patch({ progress: event.text });
          return undefined;
        case 'auth':
          // Never go back to an older copy (refresh tokens are single-use).
          if (!isOlderCodexAuth(event.auth, this.callbacks.getLocalAuth())) this.callbacks.onAuthRefresh(event.auth);
          return undefined;
        case 'done':
          finish(event.summary, event.summary.cached ? 'server-cache' : 'generated');
          return 'finished';
        case 'error':
          if (event.code === 'youtube_blocked' && canFallBack) return 'blocked';
          fail({ code: event.code, detail: event.message });
          return 'finished';
      }
    };

    type Open = (signal: AbortSignal, options: StreamOptions) => AsyncGenerator<IndexedSummarizeEvent, void, void>;

    /** One connection: 'interrupted' when it broke while the server job goes on. */
    const attempt = async (open: Open, canFallBack: boolean): Promise<Outcome | 'interrupted'> => {
      const controller = new AbortController();
      const onAbort = () => controller.abort();
      signal.addEventListener('abort', onAbort, { once: true });
      const active: ActiveStream = { controller, lastActivity: this.now(), reattachable: () => !!serverJob };
      this.activeStreams.add(active);
      const touch = () => {
        active.lastActivity = this.now();
      };
      const watchdog = setInterval(() => {
        if (serverJob && this.now() - active.lastActivity > STREAM_IDLE_TIMEOUT_MS) controller.abort();
      }, 5_000);
      try {
        const opened = () => {
          touch();
          if (this.jobs[videoId]?.reconnecting) patch({ reconnecting: undefined });
        };
        for await (const event of open(controller.signal, { onOpen: opened, onActivity: touch })) {
          if (!isCurrent()) return 'finished';
          opened();
          const outcome = handle(event, canFallBack);
          if (outcome) return outcome;
        }
        return 'interrupted'; // not reached: the stream throws when it ends early
      } catch (error) {
        if (signal.aborted) throw error;
        if (error instanceof ApiRequestError) {
          if (error.code === 'youtube_blocked' && canFallBack) return 'blocked';
          if (error.code === 'job_not_found' && serverJob) return 'lost';
        }
        if (serverJob && isConnectionError(error)) return 'interrupted';
        throw error;
      } finally {
        clearInterval(watchdog);
        this.activeStreams.delete(active);
        signal.removeEventListener('abort', onAbort);
      }
    };

    /** Follows a stream to its end, re-attaching to the server job whenever the connection breaks. */
    const follow = async (open: Open, canFallBack: boolean): Promise<Outcome> => {
      let outcome = await attempt(open, canFallBack);
      for (let failures = 0; outcome === 'interrupted'; failures++) {
        const job = serverJob!;
        if (!isCurrent()) return 'finished';
        if (frame) cancelFrame(frame);
        flushDelta();
        if (!this.jobs[videoId]?.reconnecting) patch({ reconnecting: true });
        if (this.now() - job.startedAt > JOB_LIFETIME_MS) {
          throw new ApiRequestError('stream_interrupted', 'Der Server-Auftrag ist nicht mehr erreichbar');
        }
        await this.waitForRetry(this.reconnectDelays[Math.min(failures, this.reconnectDelays.length - 1)]!, signal);
        if (!isCurrent()) return 'finished';
        outcome = await attempt(
          (attemptSignal, streamOptions) => attachSummaryJob(job.jobId, job.after, attemptSignal, streamOptions),
          canFallBack,
        );
      }
      return outcome;
    };

    /** POST /api/summarize. */
    const post = async (): Promise<Outcome> => {
      if (browserLogin) {
        // Read it now: the run before may have replaced it with a refreshed copy.
        const auth = this.callbacks.getLocalAuth();
        if (!auth) {
          fail({ code: 'codex_auth_required' });
          return 'finished';
        }
        request.codexAuth = auth;
      }
      const push = await this.pushTarget();
      if (!isCurrent()) return 'finished';
      if (push) request.push = push;
      else delete request.push;
      serverJob = undefined;
      const canFallBack = !request.transcript;
      return follow((attemptSignal, streamOptions) => summarize(request, attemptSignal, streamOptions), canFallBack);
    };

    /** The server forgot the job (restart, expired): its summary may be cached, else start once more. */
    let restarted = false;
    const recover = async (): Promise<Outcome> => {
      const lost = serverJob;
      const known = this.jobs[videoId]?.lang ?? lang;
      const cached = await getCachedSummary(videoId, known ?? langPref, signal).catch((error: unknown) => {
        if (isAbortError(error) || signal.aborted) throw error;
        return null;
      });
      if (!isCurrent()) return 'finished';
      // A regenerated summary must be newer than the job (clock skew tolerated).
      const fresh = cached && (!force || !lost || Date.parse(cached.createdAt) >= lost.startedAt - 120_000);
      if (cached && fresh) {
        // The summary of this very job: announce it like a generated one.
        finish({ ...cached, cached: false }, 'generated');
        return 'finished';
      }
      if (lost) this.store.remove(videoId, lost.jobId);
      serverJob = undefined;
      if (restarted) {
        fail({ code: 'job_not_found' });
        return 'finished';
      }
      restarted = true;
      if (resume && !(await prepare())) return 'finished';
      if (mode === 'client' && !request.transcript) {
        patch({ stage: 'transcript', stageMessage: undefined, reconnecting: undefined });
        if (!(await loadTranscript())) return 'finished';
      }
      dropDelta();
      patch({ markdown: '', progress: undefined, reconnecting: undefined, stageMessage: undefined });
      return post();
    };

    const generate = async (first: () => Promise<Outcome>): Promise<Outcome> => {
      let outcome = await first();
      while (outcome === 'lost' && isCurrent()) outcome = await recover();
      return outcome;
    };

    let releaseSlot: (() => void) | undefined;
    try {
      if (!resume) {
        // 1. Fast path: cached summary (no login needed).
        if (!force && !options.skipCache) {
          const cached = await getCachedSummary(videoId, lang ?? langPref, signal).catch((error: unknown) => {
            if (isAbortError(error) || signal.aborted) throw error;
            return null; // fall through to /api/summarize
          });
          if (!isCurrent()) return;
          if (cached) return finish({ ...cached, cached: true }, 'server-cache');
        }
        // 2. A Codex login is needed to generate.
        if (!(await prepare())) return;
      }

      // 3. Wait for a free slot.
      releaseSlot = await this.slots.acquire(signal, () => patch({ waiting: 'slot' }));
      if (!isCurrent()) return;
      patch({ waiting: undefined });

      // 4. Client mode: the browser fetches the transcript.
      if (!resume && mode === 'client') {
        patch({ stage: 'transcript', stageMessage: undefined });
        if (!(await loadTranscript())) return;
      }

      // 5. Generate (or re-attach to the job that was running before the reload).
      const first = resume
        ? () =>
            follow(
              (attemptSignal, streamOptions) => attachSummaryJob(resume.jobId, 0, attemptSignal, streamOptions),
              resume.mode !== 'client',
            )
        : post;
      if ((await generate(first)) !== 'blocked' || !isCurrent()) return;

      // 6. YouTube blocks the server: fetch the transcript here and try once more.
      dropDelta();
      if (serverJob) this.store.remove(videoId, serverJob.jobId);
      serverJob = undefined;
      patch({
        mode: 'client',
        serverBlocked: true,
        reconnecting: undefined,
        stage: 'transcript',
        stageMessage: 'YouTube blockiert den Server – lade das Transkript im Browser …',
        markdown: '',
        progress: undefined,
      });
      if (!(await loadTranscript())) return;
      patch({ notice: SERVER_BLOCKED_NOTICE, stageMessage: undefined });
      await generate(post);
    } catch (error) {
      // An aborted attempt (dead connection) that could not be re-attached is a network failure.
      if (signal.aborted) return;
      if (!isCurrent()) return;
      fail(toJobError(error));
    } finally {
      if (frame) cancelFrame(frame);
      releaseSlot?.();
    }
  }
}
